import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Loader2, ChevronLeft, Plus, Pencil, Trash2, X, Search, Mail, Phone, Building2 } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";

const CYAN = "#0891B2";
const VACIO = { nombre: "", empresa: "", email: "", telefono: "", direccion: "", etiqueta: "", notas: "" };

export default function MisClientesPage() {
  const [data, setData] = useState(undefined);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(null);

  const cargar = () => api.get("/clientes").then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!modal.form.nombre.trim()) { toast.error("El nombre es obligatorio."); return; }
    setGuardando(true);
    try {
      if (modal.mode === "crear") await api.post("/clientes", modal.form);
      else await api.put(`/clientes/${modal.id}`, modal.form);
      toast.success(modal.mode === "crear" ? "Cliente agregado." : "Cliente actualizado.");
      setModal(null); await cargar();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setGuardando(false); }
  };
  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    setBorrando(id);
    try { await api.delete(`/clientes/${id}`); await cargar(); toast.success("Cliente eliminado."); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setBorrando(null); }
  };

  const items = (data?.items || []).filter((c) => {
    const ql = q.trim().toLowerCase();
    return !ql || c.nombre.toLowerCase().includes(ql) || c.empresa.toLowerCase().includes(ql) || c.email.toLowerCase().includes(ql);
  });
  const r = data?.resumen;
  const setF = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  return (
    <div className="px-5 md:px-8 py-8 max-w-4xl" data-testid="mis-clientes-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver al Ecosistema</Link>
      <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3"><Users size={30} style={{ color: CYAN }} /> Mis clientes</h1>
          <p className="mt-2 text-slate-600">Directorio de clientes conectados a tu cuenta.</p>
        </div>
        <button onClick={() => setModal({ mode: "crear", form: { ...VACIO } })} data-testid="cliente-agregar" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold hover:brightness-110" style={{ backgroundColor: CYAN }}><Plus size={18} /> Agregar cliente</button>
      </div>

      {r && (
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[["Total", r.total], ["Con correo", r.con_email], ["Con teléfono", r.con_telefono]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 text-center"><p className="text-2xl font-extrabold text-slate-900">{v}</p><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{l}</p></div>
          ))}
        </div>
      )}

      <div className="mt-6 relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="clientes-buscar" placeholder="Buscar por nombre, empresa o correo…" className="w-full rounded-2xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
      </div>

      <div className="mt-4 space-y-2">
        {data === undefined && <div className="flex items-center gap-2 text-slate-500 py-12"><Loader2 className="animate-spin" size={16} /> Cargando…</div>}
        {data === null && <p className="text-sm text-slate-400 py-8">No se pudo cargar el directorio.</p>}
        {data && items.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="clientes-vacio">
            <Users size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">{q ? "Sin resultados." : "Aún no tienes clientes. Agrega el primero."}</p>
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-4 rounded-2xl border border-slate-100 bg-white" data-testid={`cliente-${c.id}`}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0" style={{ backgroundColor: CYAN }}>{c.nombre.charAt(0).toUpperCase()}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-slate-900 text-sm">{c.nombre}</p>
                {c.etiqueta && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">{c.etiqueta}</span>}
              </div>
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                {c.empresa && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {c.empresa}</span>}
                {c.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {c.email}</span>}
                {c.telefono && <span className="inline-flex items-center gap-1"><Phone size={11} /> {c.telefono}</span>}
              </div>
            </div>
            <button onClick={() => setModal({ mode: "editar", id: c.id, form: { nombre: c.nombre, empresa: c.empresa, email: c.email, telefono: c.telefono, direccion: c.direccion, etiqueta: c.etiqueta, notas: c.notas } })} data-testid={`cliente-editar-${c.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
            <button onClick={() => eliminar(c.id)} disabled={borrando === c.id} data-testid={`cliente-eliminar-${c.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50">{borrando === c.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}</button>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cliente-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            <h3 className="text-lg font-extrabold text-slate-900">{modal.mode === "crear" ? "Agregar cliente" : "Editar cliente"}</h3>
            <div className="mt-4 space-y-3">
              <input value={modal.form.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="cliente-form-nombre" placeholder="Nombre *" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
              <div className="grid grid-cols-2 gap-3">
                <input value={modal.form.empresa} onChange={(e) => setF("empresa", e.target.value)} data-testid="cliente-form-empresa" placeholder="Empresa" className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
                <input value={modal.form.etiqueta} onChange={(e) => setF("etiqueta", e.target.value)} data-testid="cliente-form-etiqueta" placeholder="Etiqueta (ej. VIP)" className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={modal.form.email} onChange={(e) => setF("email", e.target.value)} data-testid="cliente-form-email" placeholder="Correo" className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
                <input value={modal.form.telefono} onChange={(e) => setF("telefono", e.target.value)} data-testid="cliente-form-telefono" placeholder="Teléfono" className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
              </div>
              <input value={modal.form.direccion} onChange={(e) => setF("direccion", e.target.value)} data-testid="cliente-form-direccion" placeholder="Dirección" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
              <textarea value={modal.form.notas} onChange={(e) => setF("notas", e.target.value)} data-testid="cliente-form-notas" rows={2} placeholder="Notas" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={guardar} disabled={guardando} data-testid="cliente-guardar" className="px-5 py-2 rounded-lg text-white font-bold text-sm hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2" style={{ backgroundColor: CYAN }}>{guardando && <Loader2 size={15} className="animate-spin" />} Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
